import React, { useEffect, useMemo } from 'react';
import { Chartjs, MapLegend } from '../../../../components';
import { default as getChartConfig, getChartTitle } from './utils';
import {
	getUrlQueryParams,
	removeUrlQueryParam,
	setDefaultUrlQueryParamsForSubview,
	setUrlQueryParam,
} from '../../../../actions';
import i18n from '../../../../i18n';
import { ClassificationInfo } from '../../../../components/Modals';
import { TrendsLegend } from '../../../../components/MapLegend/subComponents';
import { trends } from '../../../../referenceData';
import { useModalContext } from '../../../../context/modalContext.jsx';

export default function ATScatterChart({ state, dispatch, urlParams, isTakingScreenshot }) {
	const [showModal] = useModalContext();

	if (!state?.indexedYml) {
		return null;
	}

	if (
		typeof urlParams.range1from === 'undefined' ||
		typeof urlParams.range1to === 'undefined' ||
		typeof urlParams.range2from === 'undefined' ||
		typeof urlParams.range2to === 'undefined'
	) {
		setDefaultUrlQueryParamsForSubview(urlParams);
	}

	const chartConfig = useMemo(() => getChartConfig(state, urlParams), [state.setUrlQueryParam, i18n.language]);

	const chartTitle = useMemo(() => getChartTitle(state), [state.setUrlQueryParam, i18n.language]);

	const chartId = isTakingScreenshot ? 'screenshotBarChart' : 'scatter-chart';

	const chartSize = useMemo(
		() => ({
			height: '745px',
		}),
		[state.setUrlQueryParam],
	);

	function updateChartJsCallback(trendToApplyToChart) {
		const event = new CustomEvent('chartjsEvent', {
			detail: {
				callback: (chart) => {
					chart.options.animations = false;
					chart.data.datasets.forEach((dataset) => {
						if (!dataset.originalBackgroundColor) {
							dataset.originalBackgroundColor = dataset.pointBackgroundColor ?? dataset.backgroundColor;
							dataset.originalPointBorderColor = dataset.pointBorderColor ?? dataset.borderColor;
						}
						dataset.pointBackgroundColor = dataset.originalBackgroundColor;
						dataset.pointBorderColor = dataset.originalPointBorderColor;
						// dataset.hidden = false; // Show all datasets
					});
					if (trendToApplyToChart !== '') {
						chart.data.datasets.forEach((dataset) => {
							if (dataset.trendClassification !== trendToApplyToChart) {
								// dataset.hidden = true; // Hide other datasets
								dataset.pointBackgroundColor = dataset.fadedBackgroundColor;
								dataset.pointBorderColor = dataset.fadedBorderColor;
							}
						});
					}

					chart.update(); // Update chart without animation
				},
			},
		});
		document.dispatchEvent(event);
	}

	const legendProps = useMemo(() => {
		const props = {};

		if (typeof urlParams.showClassifications !== 'undefined' && JSON.parse(urlParams.showClassifications) === true) {
			let selectedTrend = urlParams.highlightedTrend || '';
			// let selectedTrend = '';
			props.trends = trends();
			props.axisLegend = {
				left: i18n.t('Deterioration'),
				right: i18n.t('Improvement'),
			};
			props.onTrendsClick = (trend, urlParams) => {
				const { highlightedTrend } = urlParams;
				if (typeof highlightedTrend !== 'undefined' && highlightedTrend === trend) {
					removeUrlQueryParam({ name: 'highlightedTrend' });
				} else {
					setUrlQueryParam({ name: 'highlightedTrend', value: trend });
				}
				dispatch({
					type: 'getUrlQueryParams',
					payload: JSON.stringify(getUrlQueryParams()),
				});

				let trendToApplyToChart = '';
				if (selectedTrend === trend) {
					trendToApplyToChart = '';
				} else {
					trendToApplyToChart = trend;
				}
				selectedTrend = trendToApplyToChart;
				updateChartJsCallback(trendToApplyToChart);
			};

			props.onInfoClick = () => {
				showModal(<ClassificationInfo title={i18n.t('trend classifications')} />);
			};
		}
		return props;
	}, [state.setUrlQueryParam, i18n.language]);

	useEffect(() => {
		if (urlParams.highlightedTrend) {
			updateChartJsCallback(urlParams.highlightedTrend);
		}
	}, []);

	return (
		<>
			<Chartjs
				chartTitle={chartTitle}
				chartConfig={chartConfig}
				chartId={chartId}
				chartSize={chartSize}
				isTakingScreenshot={isTakingScreenshot}
			/>
			{legendProps.trends && (
				<TrendsLegend
					className="forGraph"
					urlParams={urlParams}
					axisLegend={legendProps.axisLegend}
					trends={legendProps.trends}
					onTrendsClick={legendProps.onTrendsClick}
					isTakingScreenshot={isTakingScreenshot}
					onInfoClick={legendProps.onInfoClick}
				/>
			)}
		</>
	);
}
